export default function PrivacyPolicy() {
  return (
    <div className="bg-white px-6 py-32 lg:px-8">
      <div className="mx-auto max-w-4xl text-base/7 text-gray-700">
        <p className="text-base/7 font-semibold text-indigo-600">Last Updated: October 21, 2025</p>
        <h1 className="mt-2 text-pretty text-4xl font-semibold tracking-tight text-gray-900 sm:text-5xl">
          Privacy Policy
        </h1>

        <div className="mt-10 space-y-8">
          {/* Introduction */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Introduction</h2>
            <p className="mb-4">
              Welcome to Wonderstood ("we," "our," or "us"). We operate a marketplace platform connecting professionals
              with clients seeking coaching, mentoring, and consulting services. We are committed to protecting your
              privacy and being transparent about how we collect, use, and share your personal information.
            </p>
            <p className="mb-4">
              This Privacy Policy explains:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>What personal information we collect and why</li>
              <li>How we use and share your information</li>
              <li>Your rights and choices regarding your information</li>
              <li>How we protect your information</li>
              <li>How to contact us with privacy questions</li>
            </ul>
            <p className="mt-4">
              By using Wonderstood, you agree to the collection and use of information in accordance with this Privacy Policy.
              If you do not agree, please do not use our services.
            </p>
          </section>

          {/* Information We Collect */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Information We Collect</h2>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">2.1 Information You Provide Directly</h3>

            <h4 className="text-lg font-semibold text-gray-900 mb-2">Account Registration Information</h4>
            <p className="mb-2">When you create an account, we collect:</p>
            <ul className="list-disc pl-6 space-y-1 mb-4">
              <li><strong>Email address</strong> - For account creation, login, and communications</li>
              <li><strong>Password</strong> - Encrypted for account security</li>
              <li><strong>Full name</strong> - To identify you on the platform</li>
              <li><strong>Account type</strong> - Whether you're a client or professional</li>
            </ul>

            <h4 className="text-lg font-semibold text-gray-900 mb-2">Professional Profile Information</h4>
            <p className="mb-2">If you register as a professional, we also collect:</p>
            <ul className="list-disc pl-6 space-y-1 mb-4">
              <li><strong>Professional title and specialization</strong> - To display your expertise</li>
              <li><strong>Years of experience</strong> - To help clients make informed decisions</li>
              <li><strong>Hourly rate</strong> - For pricing transparency</li>
              <li><strong>Professional bio and credentials</strong> - To showcase your qualifications</li>
              <li><strong>Profile photo</strong> - Optional, for visual identification</li>
              <li><strong>Availability and schedule preferences</strong> - For booking management</li>
            </ul>

            <h4 className="text-lg font-semibold text-gray-900 mb-2">Payment Information</h4>
            <p className="mb-4">
              We use Stripe, Inc. as our payment processor. When you make or receive payments, Stripe collects:
            </p>
            <ul className="list-disc pl-6 space-y-1 mb-4">
              <li><strong>Payment card information</strong> - Credit/debit card number, expiration date, CVV (collected directly by Stripe, not stored on our servers)</li>
              <li><strong>Billing address</strong> - For payment processing and fraud prevention</li>
              <li><strong>Bank account information</strong> (professionals only) - For payout processing</li>
              <li><strong>Tax identification information</strong> (professionals only) - For tax compliance and 1099 reporting</li>
            </ul>
            <p className="mb-4 italic text-gray-600">
              Note: We never see or store your complete payment card information. Stripe handles all payment data according
              to Payment Card Industry Data Security Standards (PCI-DSS). See Stripe's Privacy Policy at stripe.com/privacy.
            </p>

            <h4 className="text-lg font-semibold text-gray-900 mb-2">Communications</h4>
            <p className="mb-2">We collect information when you communicate with us or through our platform:</p>
            <ul className="list-disc pl-6 space-y-1 mb-4">
              <li><strong>Messages to other users</strong> - For booking coordination and platform communications</li>
              <li><strong>Customer support messages</strong> - To resolve your issues and improve our service</li>
              <li><strong>Email communications</strong> - Correspondence with our team</li>
            </ul>

            <h4 className="text-lg font-semibold text-gray-900 mb-2">Reviews and Ratings</h4>
            <p className="mb-2">When you leave reviews, we collect:</p>
            <ul className="list-disc pl-6 space-y-1 mb-4">
              <li><strong>Rating (1-5 stars)</strong> - Your evaluation of the professional</li>
              <li><strong>Written review</strong> - Your detailed feedback</li>
              <li><strong>Timestamp</strong> - When the review was posted</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">2.2 Information Collected Automatically</h3>

            <h4 className="text-lg font-semibold text-gray-900 mb-2">Video Call Information</h4>
            <p className="mb-4">
              We use Daily.co for video calling infrastructure. When you participate in video calls, Daily.co may collect:
            </p>
            <ul className="list-disc pl-6 space-y-1 mb-4">
              <li><strong>Call metadata</strong> - Participant identifiers, call duration, start/end times</li>
              <li><strong>Connection quality metrics</strong> - Bandwidth, latency, packet loss (for quality improvement)</li>
              <li><strong>Device information</strong> - Browser type, operating system, device type</li>
              <li><strong>IP address</strong> - For connection routing and security</li>
            </ul>
            <p className="mb-4 italic text-gray-600">
              Important: Daily.co does not record or store audio/video content of your calls unless you explicitly enable
              recording features. See Daily.co's Privacy Policy at daily.co/legal/privacy-policy.
            </p>

            <h4 className="text-lg font-semibold text-gray-900 mb-2">Usage Information</h4>
            <p className="mb-2">We automatically collect information about how you use our platform:</p>
            <ul className="list-disc pl-6 space-y-1 mb-4">
              <li><strong>Booking history</strong> - Sessions booked, attended, cancelled</li>
              <li><strong>Search queries</strong> - Professions and professionals you search for</li>
              <li><strong>Pages visited</strong> - Which pages and features you use</li>
              <li><strong>Click patterns</strong> - How you navigate through the site</li>
              <li><strong>Time spent</strong> - Duration of visits and sessions</li>
              <li><strong>Referring website</strong> - How you arrived at Wonderstood</li>
            </ul>

            <h4 className="text-lg font-semibold text-gray-900 mb-2">Device and Technical Information</h4>
            <p className="mb-2">We automatically collect technical information:</p>
            <ul className="list-disc pl-6 space-y-1 mb-4">
              <li><strong>IP address</strong> - For security, fraud detection, and approximate location</li>
              <li><strong>Browser type and version</strong> - To ensure compatibility</li>
              <li><strong>Operating system</strong> - To optimize our service</li>
              <li><strong>Device identifiers</strong> - Unique device IDs for security and fraud prevention</li>
              <li><strong>Screen resolution</strong> - To optimize display</li>
              <li><strong>Language preferences</strong> - To provide appropriate content</li>
            </ul>

            <h4 className="text-lg font-semibold text-gray-900 mb-2">Cookies and Similar Technologies</h4>
            <p className="mb-4">
              We use cookies and similar tracking technologies to improve your experience. See our Cookie Policy (Section 11) for details.
            </p>
          </section>

          {/* How We Use Your Information */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">3. How We Use Your Information</h2>
            <p className="mb-4">
              We use your personal information for the following purposes, based on our legitimate business interests,
              contractual obligations, and your consent where required:
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">3.1 To Provide Our Services</h3>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>Create and manage your account</li>
              <li>Enable booking and scheduling of sessions</li>
              <li>Process payments and payouts via Stripe</li>
              <li>Facilitate video calls via Daily.co</li>
              <li>Send booking confirmations, reminders, and notifications</li>
              <li>Provide customer support and respond to inquiries</li>
              <li>Display professional profiles and client reviews</li>
              <li>Enable messaging between clients and professionals</li>
            </ul>
            <p className="italic text-gray-600 mb-4">
              Legal Basis (GDPR): Performance of contract, legitimate interests (service provision)
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">3.2 To Improve and Personalize Our Platform</h3>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>Analyze usage patterns to improve features and user experience</li>
              <li>Personalize professional recommendations based on your interests</li>
              <li>Optimize platform performance and fix technical issues</li>
              <li>Develop new features and services</li>
              <li>Conduct research and analytics (using anonymized data)</li>
            </ul>
            <p className="italic text-gray-600 mb-4">
              Legal Basis (GDPR): Legitimate interests (platform improvement, user experience)
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">3.3 For Security and Fraud Prevention</h3>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>Detect and prevent fraud, abuse, and security breaches</li>
              <li>Verify user identity and credentials</li>
              <li>Monitor for suspicious or illegal activity</li>
              <li>Protect users from harm and enforce our Terms of Service</li>
              <li>Investigate violations of our policies</li>
              <li>Maintain logs for security monitoring</li>
            </ul>
            <p className="italic text-gray-600 mb-4">
              Legal Basis (GDPR): Legitimate interests (security, fraud prevention), legal obligation
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">3.4 For Communications</h3>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>Send transactional emails (booking confirmations, receipts, account notifications)</li>
              <li>Respond to your inquiries and support requests</li>
              <li>Send platform updates and important policy changes</li>
              <li>Send marketing emails (only with your consent - see Section 9 for opt-out)</li>
            </ul>
            <p className="italic text-gray-600 mb-4">
              Legal Basis (GDPR): Performance of contract (transactional), consent (marketing), legal obligation (policy updates)
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">3.5 For Legal Compliance</h3>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>Comply with applicable laws, regulations, and legal processes</li>
              <li>Respond to lawful requests from law enforcement and government agencies</li>
              <li>Maintain records for tax and accounting purposes</li>
              <li>Enforce our Terms of Service and other agreements</li>
              <li>Protect our legal rights and defend against legal claims</li>
            </ul>
            <p className="italic text-gray-600 mb-4">
              Legal Basis (GDPR): Legal obligation, legitimate interests (legal compliance and defense)
            </p>
          </section>

          {/* How We Share Your Information */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">4. How We Share Your Information</h2>
            <p className="mb-4">
              We do not sell your personal information. We share your information only in the following circumstances:
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">4.1 With Other Users</h3>
            <p className="mb-4">
              When you use our platform, certain information is visible to other users:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li><strong>Professional profiles are public</strong> - Name, photo, credentials, bio, ratings, reviews, and hourly rates are visible to all users</li>
              <li><strong>Client names visible to booked professionals</strong> - Professionals see the name of clients who book sessions</li>
              <li><strong>Reviews are public</strong> - Your reviews (with your first name) are visible to all users</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">4.2 With Service Providers (Data Processors)</h3>
            <p className="mb-4">
              We share data with trusted third-party service providers who help us operate our platform. These providers
              are contractually obligated to protect your data and use it only for the purposes we specify:
            </p>

            <h4 className="text-lg font-semibold text-gray-900 mb-2">Stripe, Inc. (Payment Processing)</h4>
            <ul className="list-disc pl-6 space-y-1 mb-3">
              <li><strong>Data shared:</strong> Payment information, transaction details, billing addresses, tax IDs (professionals)</li>
              <li><strong>Purpose:</strong> Payment processing, payouts, fraud prevention, regulatory compliance</li>
              <li><strong>Location:</strong> United States (Standard Contractual Clauses in place for GDPR compliance)</li>
              <li><strong>Privacy Policy:</strong> stripe.com/privacy</li>
              <li><strong>Retention:</strong> Stripe retains data per financial regulations (typically 7 years)</li>
            </ul>

            <h4 className="text-lg font-semibold text-gray-900 mb-2">Daily.co (Video Infrastructure)</h4>
            <ul className="list-disc pl-6 space-y-1 mb-3">
              <li><strong>Data shared:</strong> Call metadata, device information, IP addresses</li>
              <li><strong>Purpose:</strong> Video call facilitation, quality optimization, security</li>
              <li><strong>Location:</strong> United States</li>
              <li><strong>Privacy Policy:</strong> daily.co/legal/privacy-policy</li>
              <li><strong>Note:</strong> Daily.co does not store audio/video content unless recording is enabled</li>
            </ul>

            <h4 className="text-lg font-semibold text-gray-900 mb-2">Google LLC / Firebase (Backend Infrastructure)</h4>
            <ul className="list-disc pl-6 space-y-1 mb-3">
              <li><strong>Data shared:</strong> All user account data, profiles, bookings, messages, and usage data</li>
              <li><strong>Purpose:</strong> Database hosting, authentication, cloud storage, analytics</li>
              <li><strong>Location:</strong> United States (us-central1 region) - Standard Contractual Clauses in place</li>
              <li><strong>Privacy Policy:</strong> policies.google.com/privacy</li>
              <li><strong>Note:</strong> Firebase Authentication operates only from US data centers</li>
            </ul>

            <h4 className="text-lg font-semibold text-gray-900 mb-2">Email Service Provider</h4>
            <ul className="list-disc pl-6 space-y-1 mb-3">
              <li><strong>Data shared:</strong> Email addresses, names, email content</li>
              <li><strong>Purpose:</strong> Transactional and marketing email delivery</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">4.3 For Legal Reasons</h3>
            <p className="mb-4">We may disclose your information if required by law or if we believe in good faith that disclosure is necessary to:</p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>Comply with legal obligations, court orders, or legal processes</li>
              <li>Respond to lawful requests from government agencies or law enforcement</li>
              <li>Enforce our Terms of Service and other agreements</li>
              <li>Protect the rights, property, or safety of Wonderstood, our users, or the public</li>
              <li>Detect, prevent, or investigate fraud, security breaches, or illegal activity</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">4.4 Business Transfers</h3>
            <p className="mb-4">
              If Wonderstood is involved in a merger, acquisition, bankruptcy, reorganization, or sale of assets, your
              personal information may be transferred as part of that transaction. We will notify you via email and/or
              prominent notice on our platform before your information is transferred and becomes subject to a different privacy policy.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">4.5 With Your Consent</h3>
            <p className="mb-4">
              We may share your information for other purposes with your explicit consent.
            </p>
          </section>

          {/* Data Retention */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Data Retention</h2>
            <p className="mb-4">
              We retain your personal information only for as long as necessary to fulfill the purposes described in this
              Privacy Policy, unless a longer retention period is required or permitted by law. Specific retention periods:
            </p>

            <div className="overflow-x-auto mb-4">
              <table className="min-w-full border border-gray-300">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="border border-gray-300 px-4 py-2 text-left font-semibold">Data Category</th>
                    <th className="border border-gray-300 px-4 py-2 text-left font-semibold">Retention Period</th>
                    <th className="border border-gray-300 px-4 py-2 text-left font-semibold">Justification</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border border-gray-300 px-4 py-2">Active account data</td>
                    <td className="border border-gray-300 px-4 py-2">Until account deletion requested</td>
                    <td className="border border-gray-300 px-4 py-2">Service provision</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 px-4 py-2">Deleted account data</td>
                    <td className="border border-gray-300 px-4 py-2">90 days after deletion</td>
                    <td className="border border-gray-300 px-4 py-2">Fraud prevention, backups</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 px-4 py-2">Payment transaction records</td>
                    <td className="border border-gray-300 px-4 py-2">7 years</td>
                    <td className="border border-gray-300 px-4 py-2">Tax compliance, Stripe requirements</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 px-4 py-2">Video call metadata</td>
                    <td className="border border-gray-300 px-4 py-2">90 days</td>
                    <td className="border border-gray-300 px-4 py-2">Quality assurance, dispute resolution</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 px-4 py-2">Messages and communications</td>
                    <td className="border border-gray-300 px-4 py-2">2 years or account deletion</td>
                    <td className="border border-gray-300 px-4 py-2">Dispute resolution, user access</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 px-4 py-2">Session recordings (if enabled)</td>
                    <td className="border border-gray-300 px-4 py-2">30 days or user deletion</td>
                    <td className="border border-gray-300 px-4 py-2">Compliance, review purposes</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 px-4 py-2">Professional credentials</td>
                    <td className="border border-gray-300 px-4 py-2">Until deletion + 2 years</td>
                    <td className="border border-gray-300 px-4 py-2">Background check compliance</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 px-4 py-2">Reviews and ratings</td>
                    <td className="border border-gray-300 px-4 py-2">Permanent (anonymized after deletion)</td>
                    <td className="border border-gray-300 px-4 py-2">Platform integrity, user protection</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 px-4 py-2">Login/access logs</td>
                    <td className="border border-gray-300 px-4 py-2">1 year</td>
                    <td className="border border-gray-300 px-4 py-2">Security, fraud detection</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 px-4 py-2">Marketing cookies</td>
                    <td className="border border-gray-300 px-4 py-2">12 months</td>
                    <td className="border border-gray-300 px-4 py-2">Marketing purposes</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 px-4 py-2">Analytics data</td>
                    <td className="border border-gray-300 px-4 py-2">26 months</td>
                    <td className="border border-gray-300 px-4 py-2">Business analytics (Google Analytics standard)</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <p className="mb-4">
              <strong>Exception for Legal Obligations:</strong> We may retain certain information for longer periods if required
              by law, regulation, legal process, or to protect our legal rights.
            </p>

            <p className="mb-4">
              <strong>Reviews After Account Deletion:</strong> To maintain platform integrity and protect other users, reviews
              and ratings remain visible after account deletion but are anonymized (displayed as "Former User").
            </p>
          </section>

          {/* Data Security */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Data Security</h2>
            <p className="mb-4">
              We implement reasonable technical and organizational measures to protect your personal information from
              unauthorized access, disclosure, alteration, or destruction:
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">Security Measures</h3>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li><strong>Encryption in transit:</strong> All data transmitted between your device and our servers is encrypted using TLS/SSL (Transport Layer Security)</li>
              <li><strong>Encryption at rest:</strong> Sensitive data stored in our databases is encrypted</li>
              <li><strong>Password security:</strong> Passwords are hashed using industry-standard algorithms (never stored in plain text)</li>
              <li><strong>Access controls:</strong> Strict access controls limit employee access to personal data on a need-to-know basis</li>
              <li><strong>Two-factor authentication:</strong> Available for account protection</li>
              <li><strong>Regular security audits:</strong> We conduct periodic security assessments and vulnerability testing</li>
              <li><strong>Secure infrastructure:</strong> We use Google Cloud Platform and Firebase with enterprise-grade security</li>
              <li><strong>Payment security:</strong> Stripe handles all payment data per PCI-DSS standards</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">Your Responsibilities</h3>
            <p className="mb-4">While we take security seriously, please also protect your account:</p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>Choose a strong, unique password</li>
              <li>Enable two-factor authentication</li>
              <li>Never share your password with others</li>
              <li>Log out from shared or public devices</li>
              <li>Report suspicious activity immediately to support@wonderstood.com</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">Data Breach Notification</h3>
            <p className="mb-4">
              In the unlikely event of a data breach affecting your personal information, we will notify you within 72 hours
              of discovering the breach (as required by GDPR) or as required by applicable law. We will provide information about:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>What data was affected</li>
              <li>When the breach occurred</li>
              <li>Steps we've taken to address the breach</li>
              <li>Recommended actions you should take</li>
            </ul>

            <p className="mt-4 text-sm text-gray-600 italic">
              <strong>Important:</strong> No method of transmission over the internet or electronic storage is 100% secure.
              While we strive to protect your personal information, we cannot guarantee absolute security.
            </p>
          </section>

          {/* Your Rights and Choices */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Your Rights and Choices</h2>
            <p className="mb-4">
              Depending on your location, you may have certain rights regarding your personal information:
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">7.1 Rights for All Users</h3>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li><strong>Access your data:</strong> You can view and download most of your personal information from your account settings</li>
              <li><strong>Update your information:</strong> You can update your profile, email address, and other account information at any time</li>
              <li><strong>Delete your account:</strong> You can request account deletion from your account settings. See Section 7.5 for details</li>
              <li><strong>Opt-out of marketing emails:</strong> Click "unsubscribe" in any marketing email or manage preferences in account settings</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">7.2 Additional Rights for EU/EEA Users (GDPR)</h3>
            <p className="mb-4">If you are located in the European Union or European Economic Area, you have additional rights:</p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li><strong>Right to access:</strong> Request a copy of all personal information we hold about you</li>
              <li><strong>Right to rectification:</strong> Request correction of inaccurate or incomplete data</li>
              <li><strong>Right to erasure ("right to be forgotten"):</strong> Request deletion of your personal information (subject to legal exceptions)</li>
              <li><strong>Right to data portability:</strong> Receive your data in a machine-readable format to transfer to another service</li>
              <li><strong>Right to restrict processing:</strong> Request that we limit how we use your data in certain circumstances</li>
              <li><strong>Right to object:</strong> Object to processing based on legitimate interests or for direct marketing</li>
              <li><strong>Right to withdraw consent:</strong> Withdraw consent for processing that requires your consent (won't affect lawfulness of past processing)</li>
              <li><strong>Right to lodge a complaint:</strong> File a complaint with your local data protection authority if you believe we've violated GDPR</li>
            </ul>
            <p className="mb-4">
              To exercise these rights, email us at support@wonderstood.com. We will respond within 30 days.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">7.3 Additional Rights for California Residents (CCPA/CPRA)</h3>
            <p className="mb-4">If you are a California resident, you have specific rights under the California Consumer Privacy Act:</p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li><strong>Right to know:</strong> Request disclosure of personal information we've collected, used, and shared in the past 12 months</li>
              <li><strong>Right to deletion:</strong> Request deletion of your personal information (subject to legal exceptions)</li>
              <li><strong>Right to correction:</strong> Request correction of inaccurate personal information</li>
              <li><strong>Right to opt-out:</strong> We do not "sell" or "share" (for cross-context behavioral advertising) your personal information. If our practices change, you will have the right to opt-out</li>
              <li><strong>Right to limit use of sensitive personal information:</strong> Request that we limit use of your sensitive information (currently we use it only for disclosed purposes)</li>
              <li><strong>Right to non-discrimination:</strong> We will not discriminate against you for exercising your CCPA rights</li>
            </ul>
            <p className="mb-4">
              <strong>Authorized Agents:</strong> You may designate an authorized agent to submit requests on your behalf.
              The agent must provide written authorization, and we may require verification of your identity.
            </p>
            <p className="mb-4">
              To exercise these rights, email us at support@wonderstood.com or call us at [insert phone number].
              We will respond within 45 days.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">7.4 Verifying Your Identity</h3>
            <p className="mb-4">
              To protect your privacy, we must verify your identity before processing access, deletion, or correction requests.
              We may ask you to:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>Confirm your email address or other account information</li>
              <li>Provide additional verification documents</li>
              <li>Answer security questions</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">7.5 Account Deletion Process</h3>
            <p className="mb-4">When you delete your account:</p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>Your profile and account information are immediately deactivated and no longer visible to other users</li>
              <li>Most personal information is deleted within 90 days (some data retained for fraud prevention and legal compliance)</li>
              <li>Payment records retained for 7 years per tax regulations</li>
              <li>Reviews you've written remain visible but are anonymized (displayed as "Former User")</li>
              <li>Pending payouts will be processed if you're a professional</li>
            </ul>
            <p className="mt-4 text-sm text-gray-600 italic">
              <strong>Note:</strong> Account deletion is permanent and cannot be undone. Please download any data you wish
              to keep before deleting your account.
            </p>
          </section>

          {/* International Data Transfers */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">8. International Data Transfers</h2>
            <p className="mb-4">
              Wonderstood is based in the United States, and our service providers (Stripe, Daily.co, Firebase/Google)
              process data in the United States. If you are located outside the United States, your personal information
              will be transferred to and processed in the United States.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">Protections for EU/EEA Users</h3>
            <p className="mb-4">
              For users in the European Union or European Economic Area, we ensure adequate protection for your data through:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li><strong>Standard Contractual Clauses (SCCs):</strong> We have SCCs in place with our US-based service providers (Google/Firebase, Stripe, Daily.co) approved by the European Commission</li>
              <li><strong>Adequacy decisions:</strong> Where available, we rely on adequacy decisions recognizing certain countries as providing adequate data protection</li>
              <li><strong>Additional safeguards:</strong> Our service providers implement technical and organizational measures to protect your data</li>
            </ul>
            <p className="mb-4">
              By using Wonderstood, you consent to the transfer of your personal information to the United States and
              other countries where our service providers operate.
            </p>
          </section>

          {/* Marketing Communications */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Marketing Communications</h2>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">Transactional Emails (Cannot Opt-Out)</h3>
            <p className="mb-4">We will send you emails necessary for platform operation:</p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>Booking confirmations and reminders</li>
              <li>Payment receipts and payout notifications</li>
              <li>Account security alerts</li>
              <li>Policy updates and important platform changes</li>
              <li>Customer support responses</li>
            </ul>
            <p className="mb-4 text-sm italic text-gray-600">
              You cannot opt-out of these emails while maintaining an active account, as they are essential to the service.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">Marketing Emails (Optional)</h3>
            <p className="mb-4">With your consent, we may send:</p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>Platform feature announcements</li>
              <li>Professional recommendations</li>
              <li>Promotional offers</li>
              <li>Newsletter and tips</li>
            </ul>
            <p className="mb-4">
              <strong>Opt-Out:</strong> You can unsubscribe from marketing emails at any time by:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>Clicking "unsubscribe" at the bottom of any marketing email</li>
              <li>Managing your email preferences in account settings</li>
              <li>Emailing support@wonderstood.com</li>
            </ul>
            <p className="mb-4">
              We will process your opt-out within 10 business days as required by CAN-SPAM regulations.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">Text/SMS Messages</h3>
            <p className="mb-4">
              We will only send text messages with your explicit opt-in consent. Messages may include:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>Appointment reminders (if you opt-in)</li>
              <li>Two-factor authentication codes</li>
            </ul>
            <p className="mb-4">
              <strong>Opt-Out:</strong> Reply STOP to any text message to unsubscribe. Standard messaging rates may apply.
            </p>
          </section>

          {/* Children's Privacy */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Children's Privacy</h2>
            <p className="mb-4">
              Wonderstood is not intended for individuals under the age of 18. We do not knowingly collect personal
              information from anyone under 18 years of age.
            </p>
            <p className="mb-4">
              <strong>Age Requirement:</strong> By creating an account, you represent and warrant that you are at least
              18 years old.
            </p>
            <p className="mb-4">
              <strong>If We Learn of Underage Users:</strong> If we become aware that we have collected personal information
              from someone under 18, we will promptly delete that information and terminate the account.
            </p>
            <p className="mb-4">
              <strong>Parents/Guardians:</strong> If you believe your child under 18 has created an account or provided
              personal information to us, please contact us immediately at support@wonderstood.com so we can delete the information.
            </p>
          </section>

          {/* Cookie Policy */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Cookie Policy</h2>
            <p className="mb-4">
              We use cookies and similar tracking technologies to improve your experience, analyze usage, and provide
              personalized features.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">What Are Cookies?</h3>
            <p className="mb-4">
              Cookies are small text files stored on your device when you visit a website. They help websites remember
              your preferences and enable certain functionality.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">Types of Cookies We Use</h3>

            <h4 className="text-lg font-semibold text-gray-900 mb-2">1. Essential Cookies (Cannot Be Disabled)</h4>
            <p className="mb-2">Required for platform operation:</p>
            <ul className="list-disc pl-6 space-y-1 mb-4">
              <li>Authentication cookies (keep you logged in)</li>
              <li>Security cookies (protect against fraud and attacks)</li>
              <li>Session cookies (maintain your session state)</li>
              <li>Load balancing cookies (distribute traffic)</li>
            </ul>
            <p className="mb-4 text-sm italic text-gray-600">Duration: Session or up to 1 year</p>

            <h4 className="text-lg font-semibold text-gray-900 mb-2">2. Functional Cookies (Optional)</h4>
            <p className="mb-2">Enhance platform functionality:</p>
            <ul className="list-disc pl-6 space-y-1 mb-4">
              <li>Language preferences</li>
              <li>Display preferences</li>
              <li>Feature preferences</li>
            </ul>
            <p className="mb-4 text-sm italic text-gray-600">Duration: Up to 1 year</p>

            <h4 className="text-lg font-semibold text-gray-900 mb-2">3. Analytics Cookies (Optional)</h4>
            <p className="mb-2">Help us understand platform usage:</p>
            <ul className="list-disc pl-6 space-y-1 mb-4">
              <li>Google Analytics (tracks page views, user behavior, demographics)</li>
              <li>Firebase Analytics (tracks app usage, crashes, performance)</li>
            </ul>
            <p className="mb-4 text-sm italic text-gray-600">Duration: Up to 26 months (Google Analytics standard)</p>

            <h4 className="text-lg font-semibold text-gray-900 mb-2">4. Marketing Cookies (Optional)</h4>
            <p className="mb-2">Used for advertising and retargeting (if we implement in the future):</p>
            <ul className="list-disc pl-6 space-y-1 mb-4">
              <li>Advertising platform cookies</li>
              <li>Social media pixels</li>
              <li>Retargeting cookies</li>
            </ul>
            <p className="mb-4 text-sm italic text-gray-600">Duration: Up to 12 months</p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">Managing Cookie Preferences</h3>
            <p className="mb-4">You can control cookies through:</p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li><strong>Cookie banner:</strong> When you first visit, you can accept or reject non-essential cookies</li>
              <li><strong>Account settings:</strong> Manage your cookie preferences at any time (link in footer)</li>
              <li><strong>Browser settings:</strong> Most browsers allow you to refuse cookies or delete existing cookies.
              However, this may limit platform functionality</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">Third-Party Cookies</h3>
            <p className="mb-4">
              Some cookies are set by third parties (e.g., Google Analytics, Firebase). These are subject to the third
              parties' privacy policies:
            </p>
            <ul className="list-disc pl-6 space-y-1 mb-4">
              <li>Google Analytics: policies.google.com/privacy</li>
              <li>Firebase: firebase.google.com/support/privacy</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">Do Not Track</h3>
            <p className="mb-4">
              Some browsers support "Do Not Track" (DNT) signals. Currently, there is no industry standard for responding
              to DNT signals. We do not respond to DNT signals at this time.
            </p>
          </section>

          {/* Third-Party Links */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Third-Party Links and Services</h2>
            <p className="mb-4">
              Our platform may contain links to third-party websites, services, or integrations. We are not responsible
              for the privacy practices of these third parties.
            </p>
            <p className="mb-4">
              <strong>Examples of third-party services:</strong>
            </p>
            <ul className="list-disc pl-6 space-y-1 mb-4">
              <li>Stripe (payment processing) - stripe.com/privacy</li>
              <li>Daily.co (video infrastructure) - daily.co/legal/privacy-policy</li>
              <li>Professional's personal websites or social media profiles</li>
            </ul>
            <p className="mb-4">
              We encourage you to review the privacy policies of any third-party services you use through our platform
              or access via links from our platform.
            </p>
          </section>

          {/* Changes to This Privacy Policy */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">13. Changes to This Privacy Policy</h2>
            <p className="mb-4">
              We may update this Privacy Policy from time to time to reflect changes in our practices, technology, legal
              requirements, or other factors.
            </p>
            <p className="mb-4">
              <strong>How We Notify You:</strong>
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li><strong>Material changes:</strong> We will notify you via email and/or prominent notice on our platform
              at least 30 days before changes take effect</li>
              <li><strong>Minor changes:</strong> We will update the "Last Updated" date at the top of this policy</li>
            </ul>
            <p className="mb-4">
              <strong>Your Acceptance:</strong> Continued use of Wonderstood after changes take effect constitutes
              acceptance of the updated Privacy Policy. If you do not agree with the changes, you must stop using our
              platform and may request account deletion.
            </p>
            <p className="mb-4">
              We encourage you to periodically review this Privacy Policy to stay informed about how we protect your information.
            </p>
          </section>

          {/* Contact Us */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">14. Contact Us</h2>
            <p className="mb-4">
              If you have questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:
            </p>

            <div className="bg-gray-50 p-6 rounded-lg mb-4">
              <p className="mb-2"><strong>Support:</strong> support@wonderstood.com</p>
              {/* <p className="mb-2"><strong>Mailing Address:</strong></p>
              <p className="ml-4">
                Wonderstood, Inc.<br />
                [Street Address]<br />
                [City, State ZIP]<br />
                United States
              </p> */}
            </div>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">For EU/EEA Users</h3>
            <p className="mb-4">
              If you are located in the European Union or European Economic Area and have concerns about our data practices
              that we have not resolved, you have the right to lodge a complaint with your local data protection authority.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">Response Time</h3>
            <p className="mb-4">
              We aim to respond to all privacy inquiries within 30 days. For California residents exercising CCPA rights,
              we will respond within 45 days.
            </p>
          </section>

          {/* Additional Information */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">15. Additional Information for Specific Jurisdictions</h2>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">California Residents - "Shine the Light" Law</h3>
            <p className="mb-4">
              Under California Civil Code Section 1798.83, California residents have the right to request information
              about disclosure of personal information to third parties for direct marketing purposes. We do not disclose
              personal information to third parties for their direct marketing purposes.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">Nevada Residents</h3>
            <p className="mb-4">
              Nevada law (NRS 603A.340) permits Nevada residents to opt-out of the "sale" of personal information. We do
              not sell personal information as defined by Nevada law. If you are a Nevada resident and would like to submit
              an opt-out request as a precaution, please email support@wonderstood.com.
            </p>
          </section>

          {/* Summary */}
          <section className="bg-indigo-50 p-6 rounded-lg border border-indigo-200">
            <h2 className="text-2xl font-bold text-indigo-900 mb-4">Privacy Policy Summary</h2>
            <p className="mb-4 text-indigo-900">
              <strong>This summary is for your convenience and does not replace the full Privacy Policy above.</strong>
            </p>
            <ul className="list-disc pl-6 space-y-2 text-indigo-900">
              <li><strong>What we collect:</strong> Account info, payment data (via Stripe), video call metadata (via Daily.co), usage data, and communications</li>
              <li><strong>Why we collect it:</strong> To provide our marketplace service, process payments, facilitate video calls, improve our platform, and ensure security</li>
              <li><strong>Who we share with:</strong> Stripe (payments), Daily.co (video), Firebase/Google (infrastructure), and as required by law. We do not sell your data.</li>
              <li><strong>Your rights:</strong> Access, correct, delete your data; opt-out of marketing; data portability (EU); CCPA rights (California)</li>
              <li><strong>Data retention:</strong> Until account deletion (+ 90 days), except payment records (7 years) and other exceptions listed above</li>
              <li><strong>Security:</strong> Encryption, access controls, regular audits, PCI-DSS compliance for payments</li>
              <li><strong>Cookies:</strong> Essential (required), functional, analytics, and marketing (optional - manage in settings)</li>
              <li><strong>Age requirement:</strong> Must be 18+ to use Wonderstood</li>
              <li><strong>Contact:</strong> support@wonderstood.com for questions or requests</li>
            </ul>
          </section>

        </div>
      </div>
    </div>
  );
}
